// SwmmOutChartjs.js
import { useState, useEffect, useRef } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Filler,
  Tooltip,
  Legend,
  TimeScale
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import moment from 'moment'
import 'chartjs-adapter-moment'
 
ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  TimeScale
)
/**
 * General chart for displaying time-based data from the output file of a swmm program.
 * 
 * @param {JSON} out_data object containing time series data keyed by swmm object names.
 * @param {Array<string>} targets identifies the out_data keys used in this chart.
 * @param {string} titleText Descriptive text of the chart.
 * @param {string} legendPos position of the legend in relation to the chart
 * @param {boolean} spark true if this should be displayed as a spark chart with minimal decorations.
 * @param {boolean} showDows true if each point of data should be represented with a visible dot.
 * @returns {JSX} Chart.js Line chart for displaying time-based results from a swmm .out file.
 */
export function SwmmOutChartjs({out_data, targets, titleText='', legendPos='top', spark=false, showDots=false, lineSize=1}) {  
  const [labels, setLabels] = useState([])
  const [datasets, setDatasets] = useState([])
  const myImage = new Image(25, 25)
  myImage.src = "./daisy.png"

  // Returns a color from a random, unique color palette.
  const colorSet01 = (num) => {
    const color = (num + 12) * 137.508
    return `hsl(${color}, 50%, 40%)`
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip:{
        yAlign: 'bottom'
      },
      legend: {
        position: legendPos,
        labels: {
          boxWidth:spark?0:20, 
          font:{
            family:spark?"'Courier New', 'Courier', sans-serif":"'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
            weight:spark?'bold':'normal',
            lineHeight:spark?0.9:1.2
          }
        },
      },
      title: {
        display: titleText.length?true:false,
        text: titleText
      },
    },
    scales: {
      y: {
        position: 'left',
        reverse: false
      },
      x:{
        type:'time', 
        distribution: 'linear',
        display: spark?false:true
      },
    },
  }

  useEffect(()=>{
    let keysAll = out_data?Object.keys(out_data):[]
    if(keysAll?.length > 0 && targets !== undefined ) {
      let labelData = []
      let maxObjs = 100
      let rS1 = []

      // Create the datasets object.
      if(keysAll?.length > 0 && targets !== undefined ) {
        let datasets_maker = []
        // For each target in targets, create a data array if it exists in out_data
        targets.forEach((target, index) => {
          if(target in out_data){
            rS1[target] = []
            let keys = out_data[target]
            let keysLength = keys.length
            let falseStep = keysLength/maxObjs 

            if(falseStep < 1) falseStep = 1
            for(let i = 0; i < keysLength; i=i+Math.ceil(falseStep)){
              rS1[target].push(out_data[target][i][1])
              if(index===0){
                labelData.push(moment(out_data[target][i][0], 'MM/DD/YYYY HH:mm:ss').utc())
              }
            }
            datasets_maker.push({
              label: spark?target.padEnd(9):target,
              data: rS1[target],
              backgroundColor: colorSet01(index),
              borderColor: colorSet01(index),
              pointHitRadius: 20,
              pointRadius: spark||!showDots?0:3,
              borderWidth: spark||!showDots?lineSize:3,
              //pointStyle: myImage
            })
          }
        })
        
        setLabels(labelData)
        setDatasets(datasets_maker)
      }
    }
  },[out_data, targets, showDots, spark])

  let data = {
    labels: labels,
    datasets: datasets
  }

  const emptyData = {
    labels: labels, 
    datasets: []
  }

  return (
    <>
      <Line options={options} data={targets.length > 0?data:emptyData} />
    </>
  )
}
